import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";

/**
 * Горизонтальная полоса долей: отрезки по порядку, ширина каждого — его
 * `value` (доля единицы 0..1) от ширины полосы. Полоса декоративна
 * (`aria-hidden`): смысл несёт текст рядом, цвет в одиночку ничего не
 * сообщает. Разметка — `span`: полоса стоит и внутри кнопки, где допустим
 * только phrasing content.
 */

const barVariants = cva("flex w-full overflow-hidden rounded-full bg-muted", {
  variants: {
    size: {
      default: "h-2.5",
      sm: "h-1.5",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

/**
 * Тон ставит и фон, и цвет текста: штриховка рисуется по `currentColor`, то
 * есть полным тоном, и от фона под полосой не зависит.
 */
const segmentVariants = cva("block h-full shrink-0", {
  variants: {
    tone: {
      success: "bg-success text-success",
      destructive: "bg-destructive text-destructive",
      warning: "bg-warning text-warning",
      primary: "bg-primary text-primary",
      bid: "bg-bid text-bid",
      ask: "bg-ask text-ask",
      muted: "bg-muted-foreground text-muted-foreground",
      subtle: "bg-muted-foreground/50 text-muted-foreground/50",
    },
    /** Полоски полным тоном поверх фона тона с прозрачностью около 35 %. */
    hatched: {
      true: "bg-[repeating-linear-gradient(135deg,transparent_0_3px,currentColor_3px_5px)]",
      false: "",
    },
  },
  compoundVariants: [
    { tone: "success", hatched: true, className: "bg-success/35" },
    { tone: "destructive", hatched: true, className: "bg-destructive/35" },
    { tone: "warning", hatched: true, className: "bg-warning/35" },
    { tone: "primary", hatched: true, className: "bg-primary/35" },
    { tone: "bid", hatched: true, className: "bg-bid/35" },
    { tone: "ask", hatched: true, className: "bg-ask/35" },
    { tone: "muted", hatched: true, className: "bg-muted-foreground/35" },
    { tone: "subtle", hatched: true, className: "bg-muted-foreground/20" },
  ],
  defaultVariants: {
    tone: "muted",
    hatched: false,
  },
});

type SegmentTone = NonNullable<VariantProps<typeof segmentVariants>["tone"]>;

interface Segment {
  key: string;
  /** Доля единицы 0..1. */
  value: number;
  tone: SegmentTone;
  hatched?: boolean;
}

interface SegmentBarProps
  extends Omit<React.ComponentProps<"span">, "children">,
    VariantProps<typeof barVariants> {
  segments: readonly Segment[];
}

function formatWidth(value: number): string {
  return `${Number((value * 100).toFixed(2))}%`;
}

function SegmentBar({ segments, size = "default", className, ...props }: SegmentBarProps) {
  return (
    <span
      aria-hidden="true"
      data-slot="segment-bar"
      className={cn(barVariants({ size }), className)}
      {...props}
    >
      {segments
        .filter((segment) => segment.value > 0)
        .map((segment) => (
          <span
            key={segment.key}
            data-segment={segment.key}
            data-hatched={segment.hatched ? "true" : undefined}
            className={cn(
              segmentVariants({ tone: segment.tone, hatched: segment.hatched === true }),
            )}
            style={{ width: formatWidth(segment.value) }}
          />
        ))}
    </span>
  );
}

export { type Segment as SegmentBarSegment, SegmentBar };
